import { useCallback } from "react";

import Typography from "@/foundation/Typography/V2";

import { classNameGenerator, clsx } from "@/utils";

import { useAppDispatch } from "@/hooks";
import * as actions from "@/redux/BootCampReducer";

import styles from "./TrialCallbackSection.module.scss";

interface TrialCallbackSectionProps {
  variant?: string;
  setShowSlotModal?: (showSlotModal: boolean) => void;
}

const TrialCallbackSection: React.FC<TrialCallbackSectionProps> = ({
  variant = "",
  setShowSlotModal = () => {},
}) => {
  const dispatch = useAppDispatch();

  const cls = classNameGenerator(styles);

  const setShowOneOnOneTrialCallbackModal = useCallback(
    (data: boolean) =>
      dispatch(actions.setShowOneOnOneTrialCallbackModal(data)),
    [dispatch]
  );

  const title =
    "Can't find the time slot you want? Our team can help. Request a callback.";

  return (
    <section className={clsx(cls("trialCallbackSection"), cls(variant))}>
      <div
        className={cls("container")}
        onClick={() => {
          setShowSlotModal(false);
          setShowOneOnOneTrialCallbackModal(true);
        }}
      >
        <div className={cls("leftSection")}>
          <div className={cls("iconWrapper")}>{callbackIcon}</div>
          <div className={cls("content")}>
            <Typography variant="heading3" className={cls("title")}>
              {title}
            </Typography>
          </div>
        </div>
        <div className={cls("rightSection")}>{arrowRight}</div>
      </div>
    </section>
  );
};

export default TrialCallbackSection;

const callbackIcon = (
  <svg
    width="18"
    height="20"
    viewBox="0 0 18 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.8395 14.868C15.8395 14.4675 15.7047 14.2745 15.3333 14.1499C14.1458 13.7532 12.9564 13.3622 11.7708 12.9605C11.6536 12.916 11.5271 12.9021 11.4031 12.9199C11.279 12.9377 11.1615 12.9867 11.0616 13.0623C10.6149 13.3705 10.1581 13.6646 9.71085 13.9721C9.66932 14.0065 9.61773 14.0265 9.56387 14.029C9.51 14.0315 9.45679 14.0164 9.41223 13.986C8.66997 13.585 7.9841 13.0874 7.37252 12.5062C6.50957 11.7059 5.75353 10.8202 5.18919 9.78008C5.11517 9.64405 5.12593 9.55295 5.21323 9.42768C5.51944 8.98482 5.80794 8.53626 6.10972 8.09339C6.1828 7.99589 6.23029 7.88165 6.24787 7.76107C6.26544 7.6405 6.25253 7.51745 6.21032 7.40315C5.80478 6.20488 5.4081 5.00345 5.00446 3.80455C4.89184 3.46861 4.68306 3.32183 4.33193 3.32183C3.52402 3.32183 2.7161 3.31677 1.90819 3.32183C0.794068 3.32942 -0.00878474 4.14429 7.25692e-05 5.26158C0.0158892 7.21082 0.496082 9.04998 1.37043 10.7822C2.96474 13.9417 5.35643 16.2963 8.54548 17.8459C10.2537 18.6747 12.0606 19.1207 13.9674 19.1201C14.8854 19.1201 15.6756 18.4843 15.7971 17.5796C15.8116 17.4708 15.8199 17.3613 15.8249 17.2512V17.2455C15.83 17.1367 15.8306 17.0273 15.8294 16.9178L15.8395 16.0783C15.8395 15.6755 15.8395 15.272 15.8395 14.868Z"
      fill="#293056"
    />
    <path
      d="M15.8944 3.00574C14.8593 1.91503 13.5927 1.22416 12.1256 0.900864C11.641 0.794577 11.2184 1.10332 11.1962 1.57339C11.1785 1.96311 11.4227 2.24844 11.8668 2.34714C12.6944 2.53061 13.4612 2.8577 14.1229 3.38661C16.0209 4.905 16.7732 6.86626 16.3683 9.2628C16.2942 9.70566 16.5346 10.0853 16.944 10.1587C17.0419 10.1779 17.1427 10.1768 17.2402 10.1554C17.3377 10.134 17.4297 10.0927 17.5105 10.0342C17.5914 9.9757 17.6593 9.90116 17.7101 9.81524C17.7608 9.72933 17.7934 9.63388 17.8057 9.53485C17.8797 9.06161 17.9259 8.58585 17.9575 8.32456C17.9265 6.13807 17.2489 4.4324 15.8944 3.00574Z"
      fill="#293056"
    />
    <path
      d="M11.0702 4.61036C13.1485 5.13041 14.3392 6.94616 13.9855 9.0561C13.9109 9.49896 14.1519 9.87856 14.5587 9.95575C14.9794 10.0342 15.3533 9.76595 15.4293 9.31612C15.4843 8.98777 15.5102 8.65436 15.5488 8.32284C15.5318 5.86493 13.7894 3.69742 11.3986 3.18307C10.9399 3.08437 10.5539 3.30454 10.4609 3.7183C10.3679 4.13206 10.6128 4.49585 11.0702 4.61036Z"
      fill="#293056"
    />
    <path
      d="M10.2583 6.89191C11.2402 7.16585 11.7135 7.89595 11.5642 8.90821C11.5294 9.09874 11.5718 9.29526 11.682 9.45455C11.7921 9.61383 11.9611 9.72283 12.1516 9.75756C12.3421 9.7923 12.5386 9.74992 12.6979 9.63976C12.8572 9.52959 12.9662 9.36066 13.0009 9.17013C13.0414 8.95946 13.0547 8.74309 13.08 8.52988C13.0719 7.81634 12.8234 7.12638 12.3748 6.57146C11.9261 6.01653 11.3036 5.62904 10.6076 5.47157C10.1685 5.37414 9.79142 5.59494 9.69273 6.00871C9.59783 6.40792 9.83381 6.77613 10.2583 6.89191Z"
      fill="#293056"
    />
  </svg>
);

const arrowRight = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="#1D2939"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
